import { zoom, zoomIdentity } from "d3-zoom"
import { select } from "d3-selection"

const SCALE_EXTENT = [0.3, 3]
const ZOOM_STEP = 1.3

export function setupZoom(svg, g, { onZoomStart } = {}) {
  const zoomBehavior = zoom()
    .scaleExtent(SCALE_EXTENT)
    .filter((event) => {
      // Wheel always pans/zooms the background.
      if (event.type === "wheel") return true
      // Mouse/touch drags that START on a tree-node must NOT pan the
      // canvas — they belong to node click / drag-and-drop. Background
      // drags still pan as usual.
      return !event.target.closest(".tree-node[data-person-id]")
    })
    .on("start", () => { if (onZoomStart) onZoomStart() })
    .on("zoom", (event) => {
      g.attr("transform", event.transform)
    })

  svg.call(zoomBehavior)
  svg.on("dblclick.zoom", null) // disable double-click zoom

  return zoomBehavior
}

export function zoomIn(svg, zoomBehavior) {
  svg.transition().duration(300).call(zoomBehavior.scaleBy, ZOOM_STEP)
}

export function zoomOut(svg, zoomBehavior) {
  svg.transition().duration(300).call(zoomBehavior.scaleBy, 1 / ZOOM_STEP)
}

export function resetZoom(svg, zoomBehavior) {
  svg.transition().duration(300).call(zoomBehavior.transform, zoomIdentity)
}

export function fitAll(svg, zoomBehavior) {
  const g = svg.select("g.tree-graph__content")
  const bounds = g.node().getBBox()
  const svgEl = svg.node()
  const width = svgEl.clientWidth
  const height = svgEl.clientHeight
  const pad = 40

  const scale = Math.min(
    (width - pad * 2) / bounds.width,
    (height - pad * 2) / bounds.height,
    1
  )
  const tx = (width - bounds.width * scale) / 2 - bounds.x * scale
  const ty = (height - bounds.height * scale) / 2 - bounds.y * scale

  svg.transition().duration(500).call(
    zoomBehavior.transform,
    zoomIdentity.translate(tx, ty).scale(scale)
  )
}

// Center the SVG viewport on the node identified by nodeKey, with smooth transition.
// Returns true if found, false otherwise.
export function zoomToNode(svg, zoomBehavior, root, nodeKey, { scale = 1, duration = 600 } = {}) {
  const target = findNodeByKey(root, nodeKey)
  if (!target) return false
  const svgEl = svg.node()
  const cx = svgEl.clientWidth / 2
  const cy = svgEl.clientHeight / 2
  const tx = cx - target.x * scale
  const ty = cy - target.y * scale
  svg.transition().duration(duration).call(
    zoomBehavior.transform,
    zoomIdentity.translate(tx, ty).scale(scale)
  )
  return true
}

function findNodeByKey(root, nodeKey) {
  let found = null
  root.each(node => {
    if (node.data.nodeKey === nodeKey) found = node
  })
  if (found) return found
  if (root._orphanSubtrees) {
    for (const sub of root._orphanSubtrees) {
      sub.each(node => {
        if (node.data.nodeKey === nodeKey) found = node
      })
      if (found) return found
    }
  }
  return null
}
